import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";

// ──────────────────────────────────────────────────────────────────────────────
// Global in-platform confirmation & prompt modal.
//
// Usage:
//   const confirm = useConfirm();
//   const ok = await confirm({ title: "Delete?", message: "…", destructive: true });
//   if (!ok) return;
//
// For a text prompt (returns the entered string or null on cancel):
//   const v = await confirm({ title: "Rename", input: { placeholder: "New name" } });
//
// For a password re-entry gate (returns { password: string } or null on cancel):
//   const r = await confirm({
//     title: "Delete workspace",
//     input: { type: "password", placeholder: "Your password", required: true },
//     confirmLabel: "Delete forever", destructive: true,
//   });
// ──────────────────────────────────────────────────────────────────────────────

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [opts, setOpts] = useState(null);
  const resolverRef = useRef(null);
  const [value, setValue] = useState("");

  const confirm = useCallback((options = {}) => {
    setValue(options?.input?.defaultValue || "");
    setOpts(options);
    return new Promise((resolve) => { resolverRef.current = resolve; });
  }, []);

  const close = (result) => {
    if (resolverRef.current) resolverRef.current(result);
    resolverRef.current = null;
    setOpts(null);
    setValue("");
  };

  const onCancel = () => close(opts?.input ? null : (opts?.neutralLabel ? null : false));
  const onNeutral = () => close(false);
  const onOk = () => {
    if (opts?.input) {
      const v = (value || "").trim();
      if (opts.input.required !== false && !v) return;
      close(v);
    } else {
      close(true);
    }
  };

  // Keyboard support: Esc = cancel, Cmd/Ctrl+Enter = confirm
  useEffect(() => {
    if (!opts) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onCancel(); }
      else if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); onOk(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts, value]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {opts && (
        <ConfirmDialog
          opts={opts}
          value={value}
          setValue={setValue}
          onCancel={onCancel}
          onNeutral={onNeutral}
          onOk={onOk}
        />
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Fallback so the app never hard-crashes if provider is missing.
    // eslint-disable-next-line no-alert
    return async (o = {}) => window.confirm(o.message || o.title || "Are you sure?");
  }
  return ctx;
}

function ConfirmDialog({ opts, value, setValue, onCancel, onNeutral, onOk }) {
  const {
    title = "Are you sure?",
    message,
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    neutralLabel,
    destructive = false,
    input,
  } = opts;

  const okDisabled = input && input.required !== false && !(value || "").trim();

  return (
    <>
      <div className="side-panel-backdrop" onClick={onCancel} data-testid="confirm-backdrop" />
      <div
        role="dialog" aria-modal="true"
        className="pg-card"
        data-testid="confirm-modal"
        style={{
          position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
          width: "min(460px, 92vw)", padding: 0, zIndex: 200,
          borderRadius: 12, overflow: "hidden",
          boxShadow: "0 20px 40px rgba(0,0,0,0.18)",
        }}
      >
        <div style={{ padding: "22px 24px 18px", display: "flex", gap: 14, alignItems: "flex-start" }}>
          {destructive && (
            <div style={{
              width: 40, height: 40, flexShrink: 0, borderRadius: "50%",
              background: "rgba(220,38,38,0.10)", color: "var(--pg-red, #dc2626)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <AlertTriangle size={20} />
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ fontSize: 17, fontWeight: 600, color: "var(--pg-text)", margin: 0, lineHeight: 1.3 }}
                data-testid="confirm-title">
              {title}
            </h3>
            {message && (
              <div style={{ marginTop: 8, fontSize: 13.5, color: "var(--pg-text-mute)", lineHeight: 1.55, whiteSpace: "pre-wrap" }}
                   data-testid="confirm-message">
                {message}
              </div>
            )}
            {input && (
              <div style={{ marginTop: 14 }}>
                {input.label && (
                  <label
                    className="mono text-[10px] uppercase tracking-wider"
                    style={{ display: "block", color: "var(--pg-text-mute)", marginBottom: 6 }}
                  >
                    {input.label}
                  </label>
                )}
                <input
                  className="pg-input"
                  autoFocus
                  type={input.type || "text"}
                  placeholder={input.placeholder || ""}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  data-testid="confirm-input"
                />
                {input.hint && (
                  <div className="mono text-[10px] text-mute" style={{ marginTop: 6 }}>{input.hint}</div>
                )}
              </div>
            )}
          </div>
        </div>
        <div style={{
          display: "flex", justifyContent: "flex-end", gap: 8,
          padding: "14px 20px", borderTop: "1px solid var(--pg-border)",
          background: "var(--pg-surface-2, rgba(0,0,0,0.02))",
        }}>
          <button
            onClick={onCancel}
            className="pg-btn"
            data-testid="confirm-cancel"
          >{cancelLabel}</button>
          {neutralLabel && (
            <button
              onClick={onNeutral}
              className="pg-btn"
              data-testid="confirm-neutral"
            >{neutralLabel}</button>
          )}
          <button
            onClick={onOk}
            disabled={okDisabled}
            className={destructive ? "pg-btn pg-btn-danger" : "pg-btn pg-btn-primary"}
            data-testid="confirm-ok"
          >{confirmLabel}</button>
        </div>
      </div>
    </>
  );
}
